/**
 * Portal staff paste normal Google Drive sharing links. The dashboard converts
 * them to the embeddable /preview form before loading them for the client.
 */

const FILE_ID_PATTERNS: RegExp[] = [
  /\/file\/d\/([a-zA-Z0-9_-]{10,})/, // .../file/d/FILE_ID/view
  /\/d\/([a-zA-Z0-9_-]{10,})/, // docs.google.com/.../d/FILE_ID/edit
  /[?&]id=([a-zA-Z0-9_-]{10,})/, // open?id=FILE_ID, uc?export=download&id=FILE_ID
]

export function extractDriveFileId(url: string): string {
  const value = (url ?? '').trim()
  if (!value) return ''
  if (isDriveFolder(value)) return ''
  for (const pattern of FILE_ID_PATTERNS) {
    const match = value.match(pattern)
    if (match) return match[1]
  }
  return ''
}

export function isDriveUrl(url: string): boolean {
  return /(?:drive|docs)\.google\.com/.test((url ?? '').trim())
}

export function isDriveFolder(url: string): boolean {
  return /\/(?:drive\/)?(?:u\/\d+\/)?folders\//.test((url ?? '').trim())
}

/** Forces the Drive player UI into English rather than the viewer's region. */
function withEnglish(url: string): string {
  if (/[?&]hl=/.test(url)) return url
  return `${url}${url.includes('?') ? '&' : '?'}hl=en`
}

/** Returns an embeddable URL, or '' when the link cannot be embedded. */
export function toDrivePreviewUrl(url: string): string {
  const value = (url ?? '').trim()
  if (!value) return ''

  const fileId = extractDriveFileId(value)
  if (fileId) return `https://drive.google.com/file/d/${fileId}/preview?hl=en`

  // Already an embeddable Drive preview, or a non-Drive embed (e.g. Vimeo/YouTube embed).
  if (/\/preview(\?|$)/.test(value)) return isDriveUrl(value) ? withEnglish(value) : value
  if (!isDriveUrl(value) && /^https?:\/\//.test(value) && /embed|player|\.mp4($|\?)/.test(value)) {
    return value
  }

  return ''
}

export const EMBED_ERROR =
  'Video preview unavailable. Please check the Google Drive sharing permissions.'
